import { getActiveTimezone } from '@/utils/timezone';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  technicianPerformanceApi,
  TechnicianIssue,
  TechnicianPerformance as TechPerformance,
  IssueType,
  IssueSeverity,
  IssueStatus,
  ISSUE_TYPE_LABELS,
  SEVERITY_COLORS,
  STATUS_COLORS,
  getScoreColor,
  getScoreLabel,
  getScoreBgColor,
} from '@/services/technicianPerformanceApi';
import { branchApi } from '@/services/branchApi';
import {
  AlertTriangle,
  UserX,
  TrendingDown,
  TrendingUp,
  Filter,
  Calendar,
  Building2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertCircle,
  User,
  Phone,
  Star,
  X,
  FileWarning,
} from 'lucide-react';
import { getActiveCurrency } from '@/utils/currency';
import SimpleSelect from '@/components/common/SimpleSelect';

const ISSUE_STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'OPEN', label: 'Open' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed' },
];

const ISSUE_SEVERITY_OPTIONS = [
  { value: '', label: 'All Severity' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const ISSUE_TYPES: { value: IssueType; label: string }[] = [
  { value: 'REPEAT_SERVICE', label: 'Repeat Service' },
  { value: 'EXCESSIVE_REFUND', label: 'Excessive Refunds' },
  { value: 'NO_INVOICE', label: 'No Invoice' },
  { value: 'NOT_READY_RETURN', label: 'Not Ready Return' },
  { value: 'CANCEL_AFTER_WORK', label: 'Cancelled After Work' },
  { value: 'QUALITY_ISSUE', label: 'Quality Issue' },
  { value: 'REVENUE_LOSS', label: 'Revenue Loss' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

type TabType = 'performance' | 'issues';

export default function TechnicianPerformance() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('performance');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [branchFilter, setBranchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<IssueStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<IssueSeverity | ''>('');
  const [page, setPage] = useState(1);
  const [selectedIssue, setSelectedIssue] = useState<TechnicianIssue | null>(null);
  const [showResolveModal, setShowResolveModal] = useState(false);

  // Fetch branches
  const { data: branches } = useQuery({
    queryKey: ['branches-list'],
    queryFn: () => branchApi.getBranchList(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch issue stats
  const { data: issueStats } = useQuery({
    queryKey: ['issue-stats', branchFilter],
    queryFn: () => technicianPerformanceApi.getIssueStats(branchFilter || undefined),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch performance dashboard
  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['technician-performance', branchFilter, selectedYear, selectedMonth, page],
    queryFn: () =>
      technicianPerformanceApi.getPerformanceDashboard({
        branchId: branchFilter || undefined,
        year: selectedYear,
        month: selectedMonth,
        page,
        limit: 15,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: activeTab === 'performance',
  });

  // Fetch issues
  const { data: issuesData, isLoading: issuesLoading } = useQuery({
    queryKey: ['technician-issues', branchFilter, statusFilter, severityFilter, page],
    queryFn: () =>
      technicianPerformanceApi.getIssues({
        branchId: branchFilter || undefined,
        status: statusFilter || undefined,
        severity: severityFilter || undefined,
        page,
        limit: 15,
      }),
    staleTime: 2 * 60 * 1000,
    enabled: activeTab === 'issues',
  });

  // Calculate all performance mutation
  const calculateMutation = useMutation({
    mutationFn: () =>
      technicianPerformanceApi.calculateAllPerformance(
        branchFilter || undefined,
        selectedYear,
        selectedMonth
      ),
    onSuccess: (data) => {
      toast.success(`Performance calculated for ${data.count} technicians`);
      queryClient.invalidateQueries({ queryKey: ['technician-performance'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to calculate performance');
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(getActiveCurrency().locale, {
      style: 'currency',
      currency: getActiveCurrency().code,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="px-1 sm:px-3 md:p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Technician Performance</h1>
            <p className="text-xs sm:text-sm text-gray-500">Monitor performance metrics and issues</p>
          </div>
        </div>

        {/* Alert Banner */}
        {issueStats && (issueStats.critical > 0 || issueStats.highSeverity > 0) && (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm font-medium text-red-700">
              {issueStats.critical + issueStats.highSeverity} critical/high issues require attention
            </span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {issueStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileWarning className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{issueStats.total}</p>
                <p className="text-xs text-gray-500">Total Issues</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{issueStats.open}</p>
                <p className="text-xs text-gray-500">Open Issues</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{issueStats.critical}</p>
                <p className="text-xs text-gray-500">Critical Issues</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{issueStats.highSeverity}</p>
                <p className="text-xs text-gray-500">High Severity</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border mb-6">
        <div className="flex border-b">
          <button
            onClick={() => {
              setActiveTab('performance');
              setPage(1);
            }}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'performance'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Performance Dashboard
          </button>
          <button
            onClick={() => {
              setActiveTab('issues');
              setPage(1);
            }}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'issues'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Issues ({issueStats?.open || 0} open)
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <SimpleSelect
              value={branchFilter}
              onChange={(v) => {
                setBranchFilter(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Branches' },
                ...(branches ?? []).map((branch: any) => ({ value: branch.id, label: branch.name })),
              ]}
              placeholder="All Branches"
              aria-label="Filter by branch"
              wrapperClassName="w-full sm:w-44"
              className="focus:!ring-purple-500 focus:!border-purple-500"
            />

            {activeTab === 'performance' && (
              <>
                <SimpleSelect
                  value={String(selectedYear)}
                  onChange={(v) => setSelectedYear(parseInt(v))}
                  options={[currentYear - 1, currentYear, currentYear + 1].map((year) => ({
                    value: String(year),
                    label: String(year),
                  }))}
                  aria-label="Year"
                  wrapperClassName="w-28"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
                <SimpleSelect
                  value={String(selectedMonth)}
                  onChange={(v) => setSelectedMonth(parseInt(v))}
                  options={MONTHS.map((month) => ({ value: String(month.value), label: month.label }))}
                  aria-label="Month"
                  wrapperClassName="w-36"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
                <button
                  onClick={() => calculateMutation.mutate()}
                  disabled={calculateMutation.isPending}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {calculateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Calculate
                </button>
              </>
            )}

            {activeTab === 'issues' && (
              <>
                <SimpleSelect
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v as IssueStatus | '');
                    setPage(1);
                  }}
                  options={ISSUE_STATUS_OPTIONS}
                  placeholder="All Status"
                  aria-label="Filter by status"
                  wrapperClassName="w-full sm:w-44"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
                <SimpleSelect
                  value={severityFilter}
                  onChange={(v) => {
                    setSeverityFilter(v as IssueSeverity | '');
                    setPage(1);
                  }}
                  options={ISSUE_SEVERITY_OPTIONS}
                  placeholder="All Severity"
                  aria-label="Filter by severity"
                  wrapperClassName="w-full sm:w-44"
                  className="focus:!ring-purple-500 focus:!border-purple-500"
                />
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'performance' ? (
            <PerformanceTable
              data={performanceData?.technicians || []}
              isLoading={performanceLoading}
              formatCurrency={formatCurrency}
            />
          ) : (
            <IssuesTable
              issues={issuesData?.issues || []}
              isLoading={issuesLoading}
              onView={(issue) => setSelectedIssue(issue)}
              onResolve={(issue) => {
                setSelectedIssue(issue);
                setShowResolveModal(true);
              }}
            />
          )}

          {/* Pagination */}
          {((activeTab === 'performance' && performanceData?.pagination) ||
            (activeTab === 'issues' && issuesData?.pagination)) && (
            <Pagination
              pagination={activeTab === 'performance' ? performanceData.pagination : issuesData.pagination}
              page={page}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      {/* Issue Detail Modal */}
      {selectedIssue && !showResolveModal && (
        <IssueDetailModal issue={selectedIssue} onClose={() => setSelectedIssue(null)} />
      )}

      {/* Resolve Issue Modal */}
      {selectedIssue && showResolveModal && (
        <ResolveIssueModal
          issue={selectedIssue}
          onClose={() => {
            setShowResolveModal(false);
            setSelectedIssue(null);
          }}
          onSuccess={() => {
            setShowResolveModal(false);
            setSelectedIssue(null);
            queryClient.invalidateQueries({ queryKey: ['technician-issues'] });
            queryClient.invalidateQueries({ queryKey: ['issue-stats'] });
          }}
        />
      )}
    </div>
  );
}

// Performance Table Component
function PerformanceTable({
  data,
  isLoading,
  formatCurrency,
}: {
  data: TechPerformance[];
  isLoading: boolean;
  formatCurrency: (n: number) => string;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <UserX className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>No performance data found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Technician</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-purple-600 uppercase whitespace-nowrap">Points</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Assigned</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-green-600 uppercase whitespace-nowrap">Ready</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-red-600 uppercase whitespace-nowrap">Not Ready</th>
            <th className="px-2 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Revenue</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-orange-600 uppercase whitespace-nowrap">Repeated</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Score</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((tech) => (
              <tr key={tech.technicianId} className={`hover:bg-gray-50 ${tech.hasIssues ? 'bg-red-50/30' : ''}`}>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${tech.hasIssues ? 'bg-red-500' : 'bg-green-500'}`} />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{tech.technicianName}</p>
                      <p className="text-xs text-gray-500 truncate">{tech.branchName}</p>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
                    tech.totalPoints > 0 ? 'bg-purple-100 text-purple-700' : 'text-gray-400'
                  }`}>
                    {tech.totalPoints}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <span className="font-semibold text-gray-900">{tech.totalServices}</span>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
                    tech.readyServices > 0 ? 'bg-green-100 text-green-700' : 'text-gray-400'
                  }`}>
                    {tech.readyServices}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
                    tech.notReadyReturns > 0 ? 'bg-red-100 text-red-700' : 'text-gray-400'
                  }`}>
                    {tech.notReadyReturns}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-right">
                  <span className="font-medium text-gray-900 whitespace-nowrap">{formatCurrency(tech.totalRevenue)}</span>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
                    tech.repeatServices > 0 ? 'bg-red-100 text-red-700' : 'text-gray-400'
                  }`}>
                    {tech.repeatServices}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-center">
                  <div className="flex flex-col items-center">
                    <span className={`text-sm font-bold ${getScoreColor(tech.performanceScore)}`}>
                      {tech.performanceScore}
                    </span>
                    <span className="text-[10px] text-gray-400 leading-tight">{getScoreLabel(tech.performanceScore)}</span>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// Issues Table Component
function IssuesTable({
  issues,
  isLoading,
  onView,
  onResolve,
}: {
  issues: TechnicianIssue[];
  isLoading: boolean;
  onView: (issue: TechnicianIssue) => void;
  onResolve: (issue: TechnicianIssue) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
        <p>No issues found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Issue</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Technician</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Type</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Severity</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {issues.map((issue) => (
            <tr key={issue.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900 line-clamp-1">{issue.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(issue.createdAt).toLocaleDateString('en-IN', { timeZone: getActiveTimezone() })}
                  {issue.isAutoDetected && ' (Auto-detected)'}
                </p>
              </td>
              <td className="px-4 py-3">
                <p className="text-sm text-gray-900">{issue.technician?.user.name || 'Unknown'}</p>
              </td>
              <td className="px-4 py-3 text-center">
                <span className="text-xs text-gray-600">{ISSUE_TYPE_LABELS[issue.issueType]}</span>
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${SEVERITY_COLORS[issue.severity]}`}>
                  {issue.severity}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[issue.status]}`}>
                  {issue.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => onView(issue)}
                    className="p-1 text-gray-500 hover:text-purple-600 transition-colors"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {issue.status === 'OPEN' && (
                    <button
                      onClick={() => onResolve(issue)}
                      className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                      title="Resolve"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Pagination Component
function Pagination({
  pagination,
  page,
  setPage,
}: {
  pagination: { page: number; totalPages: number; total: number };
  page: number;
  setPage: (p: number) => void;
}) {
  if (pagination.totalPages <= 1) return null;

  return (
    <div className="mt-4 flex items-center justify-between">
      <div className="text-sm text-gray-500">
        Page {page} of {pagination.totalPages} ({pagination.total} total)
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
          disabled={page === pagination.totalPages}
          className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Issue Detail Modal
function IssueDetailModal({ issue, onClose }: { issue: TechnicianIssue; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Issue Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Status & Severity */}
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-medium border ${SEVERITY_COLORS[issue.severity]}`}>
              {issue.severity}
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[issue.status]}`}>
              {issue.status}
            </span>
            {issue.isAutoDetected && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                Auto-detected
              </span>
            )}
          </div>

          {/* Title */}
          <div>
            <h3 className="font-medium text-gray-900">{issue.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{ISSUE_TYPE_LABELS[issue.issueType]}</p>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
            <p className="text-sm text-gray-700 mt-1">{issue.description}</p>
          </div>

          {/* Technician */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Technician</label>
            <p className="text-sm text-gray-700 mt-1">{issue.technician?.user.name || 'Unknown'}</p>
          </div>

          {/* Revenue Impact */}
          {issue.revenueImpact && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Revenue Impact</label>
              <p className="text-sm text-red-600 font-medium mt-1">
                - {new Intl.NumberFormat(getActiveCurrency().locale, { style: 'currency', currency: getActiveCurrency().code }).format(Number(issue.revenueImpact))}
              </p>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Created</label>
              <p className="text-sm text-gray-700 mt-1">
                {new Date(issue.createdAt).toLocaleString('en-IN', { timeZone: getActiveTimezone() })}
              </p>
            </div>
            {issue.resolvedAt && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Resolved</label>
                <p className="text-sm text-gray-700 mt-1">
                  {new Date(issue.resolvedAt).toLocaleString('en-IN', { timeZone: getActiveTimezone() })}
                </p>
              </div>
            )}
          </div>

          {/* Resolution */}
          {issue.resolution && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Resolution</label>
              <p className="text-sm text-gray-700 mt-1">{issue.resolution}</p>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Resolve Issue Modal
function ResolveIssueModal({
  issue,
  onClose,
  onSuccess,
}: {
  issue: TechnicianIssue;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [resolution, setResolution] = useState('');
  const [status, setStatus] = useState<'RESOLVED' | 'DISMISSED'>('RESOLVED');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolution.trim()) {
      toast.error('Please provide a resolution');
      return;
    }

    setIsSubmitting(true);
    try {
      await technicianPerformanceApi.resolveIssue(issue.id, resolution, status);
      toast.success(`Issue ${status.toLowerCase()} successfully`);
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resolve issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Resolve Issue</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Issue Summary */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-medium text-gray-900 text-sm">{issue.title}</p>
            <p className="text-xs text-gray-500 mt-1">{issue.technician?.user.name}</p>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="RESOLVED"
                  checked={status === 'RESOLVED'}
                  onChange={() => setStatus('RESOLVED')}
                  className="text-purple-600"
                />
                <span className="text-sm text-gray-700">Mark as Resolved</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="DISMISSED"
                  checked={status === 'DISMISSED'}
                  onChange={() => setStatus('DISMISSED')}
                  className="text-purple-600"
                />
                <span className="text-sm text-gray-700">Dismiss</span>
              </label>
            </div>
          </div>

          {/* Resolution */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resolution Notes *
            </label>
            <textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Describe how this issue was resolved or why it's being dismissed..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
                status === 'RESOLVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {status === 'RESOLVED' ? 'Resolve Issue' : 'Dismiss Issue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
